<template>
  <div class="page-wrapper">
    <div class="content container-fluid">

      <div class="page-header">
        <div class="row">
          <div class="col-sm-12">
            <h3 class="page-title">Quản lý xe</h3>
            <ul class="breadcrumb">
              <li class="breadcrumb-item"><router-link to="/">Dashboard</router-link></li>
              <li class="breadcrumb-item active">Xe</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body d-flex flex-wrap align-items-center gap-2">
          <div class="input-group" style="max-width: 360px;">
            <input v-model.trim="searchText" type="text" class="form-control" placeholder="Tìm biển số / loại xe..." @keyup.enter="applySearch">
            <div class="input-group-append">
              <button class="btn btn-outline-secondary" @click="applySearch" :disabled="isLoading">
                <i class="fa fa-search"></i>
              </button>
            </div>
          </div>

          <div class="ml-auto d-flex align-items-center gap-2">
            <select class="form-control form-control-sm" style="width: auto" v-model.number="pageSize" @change="changeSize">
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="20">20</option>
            </select>
            <button class="btn btn-primary btn-sm" @click="openCreate">
              <i class="fa fa-plus"></i> Thêm xe
            </button>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body p-0">
          <div v-if="isLoading && items.length === 0" class="p-4 text-center">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 mb-0">Đang tải dữ liệu...</p>
          </div>
          <div v-else-if="items.length === 0" class="p-4 text-center text-muted">
            Không có dữ liệu.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-hover table-striped mb-0">
              <thead class="thead-light">
                <tr>
                  <th style="width: 80px;">Ảnh</th>
                  <th style="width: 140px;">Biển số</th>
                  <th>Loại xe</th> <th>Hãng</th>
                  <th>ODO</th>
                  <th>Nhiên liệu</th>
                  <th style="width: 150px;">Trạng thái</th> <th class="text-right" style="width: 120px;">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="x in items" :key="x.id">
                  <td>
                    <img :src="x.fileUrl || '/assets/img/placeholder-car.png'"
                         alt="thumb" class="rounded"
                         style="width:56px;height:42px;object-fit:cover;cursor:pointer;border:1px solid #eee;"
                         @click="openImagePreview(x.fileUrl)" />
                  </td>
                  <td><strong>{{ x.bienSo }}</strong></td>
                  <td>{{ x.tenLoaiXe || '-' }}</td> 
                  <td>{{ x.hangXe || '-' }}</td>
                  <td>{{ x.odoGanNhat != null ? x.odoGanNhat + ' km' : '-' }}</td>
                  <td>{{ x.loaiNhienLieu || '-' }}</td>
                  <td>
                    <span :class="['badge', statusBadge(x.trangThai)]">{{ formatTrangThai(x.trangThai) }}</span>
                  </td>
                  <td class="text-right">
                    <button class="btn btn-sm btn-outline-info mr-1" @click.stop.prevent="openView(x)" title="Xem chi tiết">
                      <i class="fa fa-eye"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-secondary mr-1" @click="openEdit(x)">
                      <i class="fa fa-pencil"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(x)">
                      <i class="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="totalPages > 1" class="p-3 d-flex justify-content-between align-items-center border-top">
            <small class="text-muted">
              Tổng: {{ totalElements }} xe — Trang {{ currentPage }}/{{ totalPages }}
            </small>
            <ul class="pagination mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a href="#" class="page-link" @click.prevent="goPage(currentPage - 1)">Trước</a>
              </li>
              <li v-for="p in totalPages" :key="p" class="page-item" :class="{ active: p === currentPage }">
                <a href="#" class="page-link" @click.prevent="goPage(p)">{{ p }}</a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a href="#" class="page-link" @click.prevent="goPage(currentPage + 1)">Sau</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Modal Thêm/Sửa -->
      <div v-if="showModal" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,.5);">
          <div class="modal-dialog modal-xl" role="document"> 
            <div class="modal-content">
              <form @submit.prevent="submitForm">
                <div class="modal-header bg-light">
                  <h5 class="modal-title font-weight-bold">
                    <i class="fa mr-2" :class="isEdit ? 'fa-pencil-alt' : 'fa-plus-circle'"></i>
                    {{ isEdit ? 'Cập nhật thông tin xe' : 'Thêm xe mới' }}
                  </h5>
                  <button type="button" class="close" @click="closeModal">
                    <span>&times;</span>
                  </button>
                </div>

                <div class="modal-body">
                  <div v-if="formError" class="alert alert-danger py-2">{{ formError }}</div>

                  <div class="row">
                    <div class="col-lg-4">
                      <h6 class="font-weight-bold">Ảnh đại diện xe</h6>
                      <p class="small text-muted">Chọn ảnh đẹp và rõ nét nhất cho xe.</p>
                      <div class="form-group">
                        <div class="text-center p-2 border rounded" style="background-color: #f8f9fa;">
                          <img :src="form.fileUrl || '/assets/img/placeholder-car.png'"
                               alt="Xem trước"
                               style="width: 100%; height: 180px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd;">
                        </div>
                      </div>
                      <div class="form-group">
                        <label>Dán URL hoặc chọn ảnh</label>
                        <div class="input-group">
                          <input v-model.trim="form.fileUrl" type="url" class="form-control" placeholder="https://...">
                          <div class="input-group-append">
                            <button type="button" class="btn btn-outline-secondary" @click="openAvatarModal">
                              <i class="fa fa-image"></i>
                            </button>
                          </div>
                        </div>
                        <small class="text-muted d-block mt-2">Chọn từ File Manager; hệ thống sẽ lưu lại URL ảnh.</small>
                      </div>
                    </div>

                    <div class="col-lg-8">
                      <h6 class="font-weight-bold">Thông tin nhận dạng</h6>
                      <hr class="mt-0">
                      <div class="form-row">
                        <div class="form-group col-md-4">
                          <label>Biển số <span class="text-danger">*</span></label>
                          <input v-model.trim="form.bienSo" type="text" class="form-control" required autofocus>
                        </div>
                        <div class="form-group col-md-4">
                          <label>Số khung</label>
                          <input v-model.trim="form.soKhung" type="text" class="form-control">
                        </div>
                        <div class="form-group col-md-4">
                          <label>Số máy</label>
                          <input v-model.trim="form.soMay" type="text" class="form-control">
                        </div>
                      </div>

                      <h6 class="font-weight-bold mt-3">Đặc điểm xe</h6>
                      <hr class="mt-0">
                      <div class="form-row">
                        <div class="form-group col-md-4">
                          <label>Loại xe <span class="text-danger">*</span></label>
                          <select v-model="form.loaiXeId" class="form-control" required :disabled="isLoadingOptions">
                            <option :value="null" disabled>
                              {{ isLoadingOptions ? 'Đang tải...' : '-- Chọn loại xe --' }}
                            </option>
                            <option v-for="opt in loaiXeOptions" :key="opt.id" :value="opt.id">
                              {{ opt.tenLoaiXe }}
                            </option>
                          </select>
                          <div v-if="!isLoadingOptions && loaiXeOptions.length === 0" class="text-danger small mt-1">
                            Vui lòng thêm Loại Xe trước.
                          </div>
                        </div>
                        <div class="form-group col-md-4">
                          <label>Hãng xe</label>
                          <input v-model.trim="form.hangXe" type="text" class="form-control">
                        </div>
                        <div class="form-group col-md-4">
                          <label>Model</label>
                          <input v-model.trim="form.model" type="text" class="form-control">
                        </div>
                        <div class="form-group col-md-4">
                          <label>Năm sản xuất</label>
                          <input v-model.number="form.namSx" type="number" class="form-control" min="1900" :max="new Date().getFullYear() + 1">
                        </div>
                        <div class="form-group col-md-4">
                          <label>Màu sơn</label>
                          <input v-model.trim="form.mauSon" type="text" class="form-control">
                        </div>
                        <div class="form-group col-md-4">
                          <label>Loại nhiên liệu</label>
                          <select v-model="form.loaiNhienLieu" class="form-control">
                            <option value="" disabled>-- Chọn --</option>
                            <option value="Xăng">Xăng</option>
                            <option value="Dầu">Dầu</option>
                            <option value="Điện">Điện</option>
                          </select>
                        </div>
                      </div>

                      <h6 class="font-weight-bold mt-3">Thông số & Tình trạng</h6>
                      <hr class="mt-0">
                      <div class="form-row">
                        <div class="form-group col-md-4">
                          <label>ODO gần nhất (km)</label>
                          <input v-model="form.odoGanNhat" type="number" step="any" class="form-control" min="0">
                        </div>
                        <div class="form-group col-md-4">
                          <label>Tiêu hao TB (lít/100km)</label>
                          <input v-model="form.tieuHaoTb" type="number" step="any" class="form-control" min="0">
                        </div>
                        <div class="form-group col-md-4">
                          <label>Dung tích bình (lít)</label>
                          <input v-model="form.dungTichBinh" type="number" step="any" class="form-control" min="0">
                        </div>
                        <div class="form-group col-md-4">
                          <label>Sức chứa tải (tấn)</label>
                          <input v-model="form.sucChuaTai" type="number" step="any" class="form-control" min="0">
                        </div>
                        <div class="form-group col-md-4">
                          <label>Trạng thái</label>
                          <select v-model="form.trangThai" class="form-control">
                            <option value="AVAILABLE">Sẵn sàng (Available)</option>
                            <option value="ASSIGNED">Đã giao (Assigned)</option>
                            <option value="IN_USE">Đang sử dụng (In Use)</option>
                            <option value="MAINTENANCE">Đang bảo dưỡng</option>
                            <option value="DECOMMISSIONED">Ngưng hoạt động</option>
                          </select>
                        </div>
                        <div class="form-group col-md-12">
                          <label>Ghi chú</label>
                          <input v-model.trim="form.ghiChu" type="text" class="form-control">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="modal-footer">
                  <button type="button" class="btn btn-outline-secondary" @click="closeModal">Hủy bỏ</button>
                  <button type="submit" class="btn btn-primary" :disabled="isLoading">
                    <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <i v-else class="fa" :class="isEdit ? 'fa-save' : 'fa-plus'"></i>
                    {{ isEdit ? 'Lưu thay đổi' : 'Thêm xe' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
      </div>

      <!-- Modal Xóa -->
      <div v-if="showDelete" class="modal fade show d-block" tabindex="-1" role="dialog" style="background: rgba(0,0,0,.5);">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Xoá xe</h5>
              <button type="button" class="close" @click="cancelDelete"><span>&times;</span></button>
            </div>
            <div class="modal-body">
              Bạn chắc chắn muốn xoá xe <strong>{{ pendingDelete?.bienSo }}</strong>? (Hành động này nên là "Ngưng hoạt động")
            </div>
            <div class="modal-footer">
              <button class="btn btn-outline-secondary" @click="cancelDelete">Huỷ</button>
              <button class="btn btn-danger" @click="doDelete" :disabled="isLoading">
                 <span v-if="isLoading" class="spinner-border spinner-border-sm"></span>
                 <i v-else class="fa fa-trash"></i> Xoá
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Xem chi tiết -->
      <div v-if="viewOpen" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,.5);" @click="closeView">
        <div class="modal-dialog modal-xl" role="document" @click.stop>
          <div class="modal-content">
            <div class="modal-header bg-light">
              <h5 class="modal-title font-weight-bold">
                <i class="fa fa-car-alt mr-2"></i>Chi tiết xe — {{ viewData?.bienSo }}
              </h5>
              <button type="button" class="close" @click="closeView"><span>&times;</span></button>
            </div>
            <div class="modal-body">
              <div class="row">
                <div class="col-lg-5 mb-3 mb-lg-0">
                  <div class="text-center">
                    <img :src="viewData?.fileUrl || '/assets/img/placeholder-car.png'"
                         alt="Ảnh xe"
                         style="width: 100%; height: 250px; object-fit: cover; border-radius: 8px; border: 1px solid #ddd; cursor: pointer;"
                         @click="openImagePreview(viewData?.fileUrl)" />
                    <div class="mt-2" v-if="viewData?.fileUrl">
                      <a :href="viewData.fileUrl" target="_blank" rel="noopener" class="btn btn-sm btn-outline-info">
                        <i class="fa fa-external-link-alt mr-1"></i> Mở ảnh gốc
                      </a>
                    </div>
                  </div>
                </div>

                <div class="col-lg-7">
                  <div class="card mb-3">
                    <div class="card-header font-weight-bold">
                      <i class="fa fa-info-circle mr-2"></i>Thông tin chung
                    </div>
                    <ul class="list-group list-group-flush">
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-car-side fa-fw mr-2 text-muted"></i>Loại xe</span>
                        <span class="font-weight-bold">{{ viewData?.tenLoaiXe || '-' }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-copyright fa-fw mr-2 text-muted"></i>Hãng xe</span>
                        <span class="font-weight-bold">{{ viewData?.hangXe || '-' }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-car fa-fw mr-2 text-muted"></i>Model</span>
                        <span class="font-weight-bold">{{ viewData?.model || '-' }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-barcode fa-fw mr-2 text-muted"></i>Số máy</span>
                        <span>{{ viewData?.soMay || '-' }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-barcode fa-fw mr-2 text-muted"></i>Số khung</span>
                        <span>{{ viewData?.soKhung || '-' }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-palette fa-fw mr-2 text-muted"></i>Màu sơn</span>
                        <span>{{ viewData?.mauSon || '-' }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-calendar-alt fa-fw mr-2 text-muted"></i>Năm sản xuất</span>
                        <span>{{ viewData?.namSx || '-' }}</span>
                      </li>
                    </ul>
                  </div>

                  <div class="card mb-3">
                    <div class="card-header font-weight-bold">
                      <i class="fa fa-cogs mr-2"></i>Thông số kỹ thuật
                    </div>
                    <ul class="list-group list-group-flush">
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-gas-pump fa-fw mr-2 text-muted"></i>Nhiên liệu</span>
                        <span>{{ viewData?.loaiNhienLieu || '-' }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-tachometer-alt fa-fw mr-2 text-muted"></i>Mức tiêu hao (lít/100km)</span>
                        <span>{{ viewData?.tieuHaoTb || '-' }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-fill-drip fa-fw mr-2 text-muted"></i>Dung tích bình (lít)</span>
                        <span>{{ viewData?.dungTichBinh || '-' }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-road fa-fw mr-2 text-muted"></i>ODO gần nhất</span>
                        <span>{{ viewData?.odoGanNhat ? viewData.odoGanNhat + ' km' : '-' }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-weight-hanging fa-fw mr-2 text-muted"></i>Sức chứa tải (tấn)</span>
                        <span>{{ viewData?.sucChuaTai || '-' }}</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <p class="mb-1">
                      <i class="fa fa-check-circle fa-fw mr-2 text-muted"></i>
                      <strong>Trạng thái:</strong>
                      <span :class="['badge', statusBadge(viewData?.trangThai)]" class="float-right">{{ formatTrangThai(viewData?.trangThai) }}</span>
                    </p>
                    <p class="mb-0">
                      <i class="fa fa-sticky-note fa-fw mr-2 text-muted"></i>
                      <strong>Ghi chú:</strong> {{ viewData?.ghiChu || 'Không có' }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary" @click="closeView">
                <i class="fa fa-times mr-1"></i>Đóng
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Preview ảnh lớn -->
      <div v-if="previewOpen" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,.75);">
        <div class="modal-dialog modal-dialog-centered" style="max-width: 900px;">
          <div class="modal-content bg-transparent border-0 shadow-none">
            <div class="d-flex justify-content-end">
              <button class="btn btn-light btn-sm mb-2" @click="closeImagePreview">
                <i class="fa fa-times"></i>
              </button>
            </div>
            <div class="p-2 d-flex justify-content-center">
              <img
                :src="previewUrl"
                alt="preview"
                style="max-width:100%;max-height:80vh;object-fit:contain;border-radius:8px;background:#fff;"
                @click.stop
              />
            </div>
            <div class="text-center mt-2">
              <a :href="previewUrl" target="_blank" rel="noopener" class="btn btn-outline-light btn-sm">
                Mở ảnh trong tab mới
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- File Manager Modal: dùng chung để chọn ảnh avatar xe -->
      <FileManagerModal
        v-if="showFileManagerModal"
        :folderTabs="imageTabs"
        @close="handleFmClose"
        @file-selected="onFmFileSelected"
        @tab-created="onFmTabCreated"
        @tab-deleted="onFmTabDeleted"
      />

    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'pinia'
import { useXeStore } from '@/stores/xeStore'
import api from '@/api'
import FileManagerModal from '@/components/FileManagerModal.vue'

const DEFAULT_FORM = {
  bienSo: '',
  soKhung: '',
  soMay: '',
  loaiXeId: null,
  hangXe: '',
  model: '',
  namSx: null,
  mauSon: '',
  loaiNhienLieu: '',
  dungTichBinh: '',
  tieuHaoTb: '',
  sucChuaTai: '',
  odoGanNhat: '',
  trangThai: 'AVAILABLE',
  ghiChu: '',
  fileUrl: '',
}

export default {
  name: 'QuanLyXe',
  components: { FileManagerModal },
  data() {
    return {
      searchText: '',
      showModal: false,
      isEdit: false,
      formError: null,
      form: { ...DEFAULT_FORM },
      showDelete: false,
      pendingDelete: null,

      // xem chi tiết
      viewOpen: false,
      viewData: null,

      // preview ảnh
      previewOpen: false,
      previewUrl: '',

      // FileManagerModal state (thay thế picker tự code)
      showFileManagerModal: false,
      imageTabs: [],
      fmTarget: null, // 'avatar'
    }
  },
  computed: {
    ...mapState(useXeStore, [
      'isLoading', 'error', 'items', 'totalPages', 'totalElements', 'size', 'q',
      'loaiXeOptions', 'isLoadingOptions'
    ]),
    ...mapGetters(useXeStore, ['currentPage']),
    pageSize: { get() { return this.size }, set(_) {} },
  },
  methods: {
    ...mapActions(useXeStore, [
      'fetchList', 'setQuery', 'setPage', 'setSize', 'createXe', 'updateXe', 'fetchLoaiXeOptions'
    ]),

    // ===== File Manager helpers =====
    async fetchFolders() {
      try {
        const res = await api.get('/api/images/folders')
        this.imageTabs = (res.data || []).map((folderName, index) => ({
          id: String(index + 1),
          name: folderName,
          folderName,
        }))
      } catch (e) {
        this.imageTabs = []
        console.error('[fetchFolders] lỗi tải folders', e)
        alert('Lỗi khi tải danh sách folder ảnh.')
      }
    },
    openAvatarModal() {
      this.fmTarget = 'avatar'
      this.fetchFolders().finally(() => {
        this.showFileManagerModal = true
        document.body.style.overflow = 'hidden'
      })
    },
    handleFmClose() {
      this.showFileManagerModal = false
      this.fmTarget = null
      if (!this.showModal && !this.viewOpen && !this.previewOpen) {
        document.body.style.overflow = ''
      }
    },
    onFmTabCreated(newTab) { this.imageTabs.push(newTab) },
    onFmTabDeleted(tabId) { this.imageTabs = this.imageTabs.filter(t => t.id !== tabId) },
    onFmFileSelected(image) {
      if (!image || !image.url) {
        alert('Không thể chọn ảnh: URL không hợp lệ.')
        return
      }
      if (this.fmTarget === 'avatar') {
        this.form.fileUrl = image.url
      }
      this.handleFmClose()
    },

    // ===== bảng, phân trang =====
    applySearch() { this.setQuery(this.searchText); this.fetchList() },
    changeSize() { this.setSize(Number(this.pageSize)); this.fetchList() },
    goPage(p) {
      if (p < 1 || p > this.totalPages || p === this.currentPage) return
      this.setPage(p)
      this.fetchList()
    },

    // ===== xem chi tiết =====
    openView(row) {
      if (!row) return
      this.viewData = row
      this.viewOpen = true
      this.showModal = false // đảm bảo KHÔNG mở modal sửa
      document.body.style.overflow = 'hidden'
    },
    closeView() { this.viewOpen = false; this.viewData = null; if (!this.showModal && !this.previewOpen) document.body.style.overflow = '' },

    // ===== thêm / sửa =====
    openCreate() {
      this.isEdit = false
      this.formError = null
      this.form = { ...DEFAULT_FORM }
      this.showModal = true
      document.body.style.overflow = 'hidden'
      this.fetchLoaiXeOptions()
    },
    openEdit(row) {
      this.isEdit = true
      this.formError = null
      this.form = {
        bienSo: row.bienSo,
        soKhung: row.soKhung,
        soMay: row.soMay,
        loaiXeId: row.loaiXeId,
        hangXe: row.hangXe,
        model: row.model,
        namSx: row.namSx,
        mauSon: row.mauSon,
        loaiNhienLieu: row.loaiNhienLieu,
        dungTichBinh: row.dungTichBinh ?? '',
        tieuHaoTb: row.tieuHaoTb ?? '',
        sucChuaTai: row.sucChuaTai ?? '',
        odoGanNhat: row.odoGanNhat ?? '',
        trangThai: row.trangThai || 'AVAILABLE',
        ghiChu: row.ghiChu,
        fileUrl: row.fileUrl || '',
      }
      this.form._id = row.id
      this.showModal = true
      document.body.style.overflow = 'hidden'
      this.fetchLoaiXeOptions()
    },
    closeModal() {
      this.showModal = false
      if (!this.viewOpen && !this.previewOpen) document.body.style.overflow = ''
    },

    // ===== submit =====
    toNum(val) {
      if (val === '' || val === null || val === undefined) return null
      const n = parseFloat(String(val).replace(',', '.'))
      return Number.isNaN(n) ? null : n
    },
    async submitForm() {
      this.formError = null
      if (!this.form.bienSo || !this.form.bienSo.trim()) { this.formError = 'Biển số là bắt buộc.'; return }
      if (!this.form.loaiXeId) { this.formError = 'Vui lòng chọn Loại xe.'; return }
      try {
        const url = this.form.fileUrl?.trim() || null
        const payload = {
          bienSo: this.form.bienSo?.trim(),
          soKhung: this.form.soKhung?.trim(),
          soMay: this.form.soMay?.trim(),
          loaiXeId: this.form.loaiXeId,
          hangXe: this.form.hangXe?.trim(),
          model: this.form.model?.trim(),
          namSx: this.form.namSx,
          mauSon: this.form.mauSon?.trim(),
          loaiNhienLieu: this.form.loaiNhienLieu,
          dungTichBinh: this.toNum(this.form.dungTichBinh),
          tieuHaoTb: this.toNum(this.form.tieuHaoTb),
          sucChuaTai: this.toNum(this.form.sucChuaTai),
          odoGanNhat: this.toNum(this.form.odoGanNhat),
          trangThai: this.form.trangThai,
          ghiChu: this.form.ghiChu?.trim(),
          fileUrl: url,
        }
        if (this.isEdit) await this.updateXe(this.form._id, payload)
        else await this.createXe(payload)
        this.closeModal()
        alert(this.isEdit ? 'Cập nhật xe thành công!' : 'Thêm xe thành công!')
      } catch (e) {
        this.formError = e?.response?.data?.message
          || (typeof e?.response?.data === 'string' ? e.response.data : null)
          || e?.message
          || 'Có lỗi xảy ra khi lưu thông tin xe.'
        console.error('Lỗi submit form xe:', e)
      }
    },

    // ===== xóa (mềm) =====
    confirmDelete(row) { this.pendingDelete = row; this.showDelete = true; document.body.style.overflow = 'hidden' },
    async doDelete() {
      if (!this.pendingDelete) return
      try {
        const payload = { ...this.pendingDelete, trangThai: 'DECOMMISSIONED' }
        const requestPayload = {
          bienSo: payload.bienSo,
          soKhung: payload.soKhung,
          soMay: payload.soMay,
          loaiXeId: payload.loaiXeId,
          hangXe: payload.hangXe,
          model: payload.model,
          namSx: payload.namSx,
          mauSon: payload.mauSon,
          loaiNhienLieu: payload.loaiNhienLieu,
          dungTichBinh: this.toNum(payload.dungTichBinh),
          tieuHaoTb: this.toNum(payload.tieuHaoTb),
          sucChuaTai: this.toNum(payload.sucChuaTai),
          odoGanNhat: this.toNum(payload.odoGanNhat),
          trangThai: 'DECOMMISSIONED',
          ghiChu: payload.ghiChu,
          fileUrl: payload.fileUrl,
        }
        await this.updateXe(this.pendingDelete.id, requestPayload)
        this.cancelDelete()
        alert('Đã ngưng hoạt động xe thành công.')
      } catch (e) {
        alert(e?.response?.data?.message || e?.message || 'Thao tác thất bại.')
        console.error('Lỗi xóa/ngưng hoạt động xe:', e)
      }
    },
    cancelDelete() { this.showDelete = false; this.pendingDelete = null; if (!this.showModal && !this.viewOpen && !this.previewOpen) document.body.style.overflow = '' },

    // ===== preview ảnh =====
    openImagePreview(url) { if (!url) return; this.previewUrl = url; this.previewOpen = true; document.body.style.overflow = 'hidden' },
    closeImagePreview() { this.previewOpen = false; this.previewUrl = ''; if (!this.showModal && !this.viewOpen) document.body.style.overflow = '' },

    // ===== badges =====
    statusBadge(st) {
      switch (st) {
        case 'AVAILABLE': return 'badge-success'
        case 'ASSIGNED': return 'badge-secondary'
        case 'IN_USE': return 'badge-primary'
        case 'ON_TASK': return 'badge-info'
        case 'MAINTENANCE': return 'badge-warning'
        case 'DECOMMISSIONED': return 'badge-danger'
        default: return 'badge-light'
      }
    },
    formatTrangThai(st) {
      switch (st) {
        case 'AVAILABLE': return 'Sẵn sàng'
        case 'ASSIGNED': return 'Đã giao'
        case 'IN_USE': return 'Đang sử dụng'
        case 'ON_TASK': return 'Đang chạy đơn'
        case 'MAINTENANCE': return 'Bảo dưỡng'
        case 'DECOMMISSIONED': return 'Ngưng hoạt động'
        default: return st || 'N/A'
      }
    },
  },
  mounted() {
    this.searchText = this.q
    this.fetchList()
  }
}
</script>

<style scoped>
.bg-primary{ background-color: #007bff!important; }
.table td, .table th { vertical-align: middle; }
.modal { overflow-y: auto; }
body.modal-open { overflow: hidden !important; padding-right: 0 !important; }
.badge.badge-success { background-color: #198754 !important; color: #fff !important; }
.badge.badge-warning { background-color: #ffc107 !important; color: #000 !important; }
.badge.badge-danger { background-color: #dc3545 !important; color: #fff !important; }
.badge.badge-primary { background-color: #0d6efd !important; color: #fff !important; }
.badge.badge-secondary { background-color: #6c757d !important; color: #fff !important; }
.badge.badge-light { background-color: #f8f9fa !important; color: #000 !important; border: 1px solid #dee2e6; }
.btn .spinner-border { width: 1em; height: 1em; vertical-align: text-bottom; }
.modal.fade.show.d-block{ z-index:1060; } /* đảm bảo modal xem chi tiết nổi cao */
</style>
